import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { Header } from './components/layouts/header/header';
import { Mobile } from './components/layouts/mobile/mobile';
import { Searchbox } from './components/layouts/searchbox/searchbox';
import {
  Router,
  RouterOutlet,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Footer } from './components/layouts/footer/footer';
import { LoaderComponent } from './components/layouts/loader/loader.component';
import { LoaderService } from './services/loader.service';
import { RtlStyleService } from './services/rtl-style-service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    Header,
    Mobile,
    Searchbox,
    TranslateModule,
    Footer,
    LoaderComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit {
  protected readonly title = signal('arp');
  mobileMenuOpen = false;

  private translate = inject(TranslateService);
  private rtlStyleService = inject(RtlStyleService);
  private router = inject(Router);
  private loader = inject(LoaderService);

  currentLang = 'fr';

  ngOnInit() {
    // Cache le mini-loader HTML statique (si présent)
    const staticLoader = document.getElementById('app-loader');
    if (staticLoader) staticLoader.style.display = 'none';

    // ── Loader sur navigation entre pages ──────────────────────────────
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loader.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        // Petit délai pour laisser le composant de la nouvelle page se rendre
        setTimeout(() => this.loader.hide(), 100);
      }
    });

    // Configuration des langues
    this.translate.addLangs(['fr', 'ar', 'en']);
    this.translate.setDefaultLang('fr');
    this.translate.setFallbackLang('fr');

    // Récupérer la langue sauvegardée ou utiliser celle du navigateur
    const savedLang = localStorage.getItem('preferredLanguage');
    const browserLang = this.translate.getBrowserLang();

    const lang = savedLang || (browserLang?.match(/fr|ar|en/) ? browserLang : 'fr');
    this.translate.use(lang);
    const isRtl: boolean = lang === 'ar';
    this.rtlStyleService.initStyle(isRtl);
  }

  switchLanguage(lang: string) {
    this.translate.use(lang || 'fr');
    this.currentLang = lang;
    localStorage.setItem('preferredLanguage', lang);
    this.updateDirection(lang);
    const isRtl: boolean = lang === 'ar';
    this.rtlStyleService.initStyle(isRtl);
  }

  updateDirection(lang: string) {
    const htmlTag = document.documentElement;
    if (lang === 'ar') {
      htmlTag.setAttribute('dir', 'rtl');
      htmlTag.setAttribute('lang', 'ar');
    } else {
      htmlTag.setAttribute('dir', 'ltr');
      htmlTag.setAttribute('lang', lang);
    }
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu() {
    this.mobileMenuOpen = false;
  }
}
